import React from "react";
import DayPicker from "./DayPicker";
import ClassNames from "classnames";
import Tooltip from "../../Tooltip/Tooltip";

type DayOfMonthSelectionSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

interface DayOfMonthSelectionProps {
  className?: string;
  "data-testid"?: string;
  onChange: (days: number[]) => void;
  selectedDays?: number[];
  multi?: boolean;
  label?: React.ReactNode;
  hideLabelHelper?: boolean;
  labelTop?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: DayOfMonthSelectionSize;
  labelWidth?: number | string | false;
  isInline?: boolean;
  isLarge?: boolean;
  noMargin?: boolean;
  error?: boolean;
  withHelp?: string;
  suffixIconButton?: React.ReactNode;
}

const allDays = Array.from({ length: 31 }, (_, i) => {
  return i + 1;
});

const DayOfMonthSelection: React.FC<DayOfMonthSelectionProps> = ({
  className,
  "data-testid": dataTestId,
  onChange,
  selectedDays = [],
  multi = false,
  label = "",
  hideLabelHelper = false,
  labelTop = false,
  required = true,
  disabled = false,
  readOnly = false,
  leftIcon = null,
  rightIcon = null,
  size = "m",
  labelWidth = false,
  isInline = false,
  isLarge = false,
  noMargin = false,
  error,
  withHelp = "",
  suffixIconButton = null,
}) => {
  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-weekday-selection",
    // wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
      "has-suffix-icon-btn": suffixIconButton,
    },
    className
  );
  const onDayClick = (weekday: number) => {
    if (multi) {
      if (selectedDays.includes(weekday)) {
        onChange(selectedDays.filter((day) => day !== weekday));
      } else {
        onChange([...selectedDays, weekday]);
      }
    } else {
      onChange([weekday]);
    }
  };

  return (
    <div
      className={`day-of-month-selection ${classNameWrapper}`}
      data-testid={`day-of-month-selection`}
    >
      {label && (
        <label
          className={`input-label`}
          style={{ width: labelWidth ? labelWidth : "" }}
          data-testid={`${dataTestId}-label`}
        >
          {label}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
          {withHelp && (
            <>
              <Tooltip type="info" content={withHelp}>
                <i className={`seem-icon seem-icon-help`} />
              </Tooltip>
            </>
          )}
        </label>
      )}
      {allDays.map((dayOfMonth, index) => (
        <div key={index}>
          <DayPicker
            label={dayOfMonth}
            selected={selectedDays.includes(dayOfMonth)}
            onClick={() => onDayClick(dayOfMonth)}
            title={dayOfMonth}
          />
        </div>
      ))}
    </div>
  );
};

export default DayOfMonthSelection;
